export type AppLocale = 'pt-BR' | 'en-US';

const LOCALE_STORAGE_KEY = 'pm_locale';
const DEFAULT_LOCALE: AppLocale = 'pt-BR';

const messages: Record<AppLocale, Record<string, string>> = {
  'pt-BR': {
    'errors.common.internal': 'Erro interno inesperado.',
    'errors.auth.invalid_credentials': 'Credenciais inválidas.',
    'errors.auth.token_expired': 'Sua sessão expirou. Faça login novamente.',
    'errors.auth.refresh_invalid': 'Não foi possível renovar a sessão.',
    'errors.auth.forbidden': 'Você não tem permissão para esta ação.',
    'errors.billing.limit_exceeded': 'Limite do plano atingido.',
    'errors.billing.workspace_blocked':
      'Workspace bloqueado por pendência de pagamento.',
    'errors.billing.workspace_expired':
      'Assinatura encerrada para este workspace.',
    'errors.billing.webhook_processing_failed':
      'Falha ao processar evento de cobrança.',
    'errors.tasks.not_found': 'Tarefa não encontrada.',
    'errors.tasks.validation_failed': 'Dados inválidos para a tarefa.',
    'errors.tasks.forbidden': 'Você não tem permissão para esta tarefa.',
    'errors.chat.channel_not_found': 'Canal não encontrado.',
    'errors.chat.message_not_found': 'Mensagem não encontrada.',
    'errors.chat.forbidden': 'Você não tem permissão para esta ação no chat.',
    'ui.loading.default': 'Carregando...',
    'ui.loading.permissions': 'Carregando permissões...',
    'ui.error.title': 'Ocorreu um erro na interface',
    'ui.error.retry': 'Tentar novamente',
  },
  'en-US': {
    'errors.common.internal': 'Unexpected internal error.',
    'errors.auth.invalid_credentials': 'Invalid credentials.',
    'errors.auth.token_expired': 'Your session expired. Please sign in again.',
    'errors.auth.refresh_invalid': 'Could not refresh your session.',
    'errors.auth.forbidden': 'You do not have permission for this action.',
    'errors.billing.limit_exceeded': 'Plan limit reached.',
    'errors.billing.workspace_blocked': 'Workspace is blocked due to billing.',
    'errors.billing.workspace_expired': 'Workspace subscription has expired.',
    'errors.billing.webhook_processing_failed':
      'Failed to process billing event.',
    'errors.tasks.not_found': 'Task not found.',
    'errors.tasks.validation_failed': 'Invalid task data.',
    'errors.tasks.forbidden': 'You do not have permission for this task.',
    'errors.chat.channel_not_found': 'Channel not found.',
    'errors.chat.message_not_found': 'Message not found.',
    'errors.chat.forbidden': 'You do not have permission for this chat action.',
    'ui.loading.default': 'Loading...',
    'ui.loading.permissions': 'Loading permissions...',
    'ui.error.title': 'An interface error occurred',
    'ui.error.retry': 'Try again',
  },
};

export function getCurrentLocale(): AppLocale {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }

  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored === 'pt-BR' || stored === 'en-US') {
    return stored;
  }

  const browser = window.navigator.language;
  if (browser.toLowerCase().startsWith('en')) {
    return 'en-US';
  }

  return DEFAULT_LOCALE;
}

export function setCurrentLocale(locale: AppLocale): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}

export function t(key: string, fallback?: string): string {
  const locale = getCurrentLocale();
  return messages[locale][key] || fallback || key;
}

export function formatDate(
  value: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(getCurrentLocale(), options).format(date);
}

export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(getCurrentLocale(), options).format(value);
}

